import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CloudRain, MagnifyingGlass, ClockCounterClockwise, CreditCard, SignOut, ArrowsLeftRight, ListBullets, FilePdf, Code } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const Dashboard = () => {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white mobile-nav-padding">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <CloudRain size={28} weight="bold" className="text-blue-600" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
            <div className="flex items-center gap-3 sm:gap-6">
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  data-testid="admin-dashboard-link"
                  className="text-sm text-blue-600 hover:text-blue-700 transition-colors font-medium hidden sm:block"
                >
                  Admin Panel
                </Link>
              )}
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-sm text-slate-600 hover:text-slate-950 transition-colors flex items-center gap-1.5"
              >
                <SignOut size={16} weight="bold" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-slate-950" style={{ fontFamily: 'Chivo' }}>Welcome back, {user?.name}</h1>
          <p className="text-sm text-slate-600 mt-2">Professional weather verification at your fingertips</p>
        </div>

        {/* Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/lookup"
            data-testid="dashboard-lookup-card"
            className="group bg-white p-8 border border-slate-200 hover:border-blue-600 transition-colors"
          >
            <div className="w-12 h-12 bg-blue-50 border border-blue-200 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
              <MagnifyingGlass size={24} weight="bold" className="text-blue-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-xl font-semibold text-slate-950 mb-2" style={{ fontFamily: 'Chivo' }}>New Lookup</h3>
            <p className="text-sm text-slate-600">Retrieve historical weather conditions for any location and time</p>
          </Link>

          <Link
            to="/compare"
            data-testid="dashboard-compare-card"
            className="group bg-white p-8 border border-slate-200 hover:border-blue-600 transition-colors"
          >
            <div className="w-12 h-12 bg-blue-50 border border-blue-200 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
              <ArrowsLeftRight size={24} weight="bold" className="text-blue-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-xl font-semibold text-slate-950 mb-2" style={{ fontFamily: 'Chivo' }}>Compare Weather</h3>
            <p className="text-sm text-slate-600">Compare two locations or dates side by side</p>
          </Link>

          <Link
            to="/bulk"
            data-testid="dashboard-bulk-card"
            className="group bg-white p-8 border border-slate-200 hover:border-blue-600 transition-colors"
          >
            <div className="w-12 h-12 bg-blue-50 border border-blue-200 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
              <ListBullets size={24} weight="bold" className="text-blue-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-xl font-semibold text-slate-950 mb-2" style={{ fontFamily: 'Chivo' }}>Bulk Lookup</h3>
            <p className="text-sm text-slate-600">Look up weather for multiple addresses at once</p>
          </Link>

          <Link
            to="/history"
            data-testid="dashboard-history-card"
            className="group bg-white p-8 border border-slate-200 hover:border-blue-600 transition-colors"
          >
            <div className="w-12 h-12 bg-blue-50 border border-blue-200 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
              <ClockCounterClockwise size={24} weight="bold" className="text-blue-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-xl font-semibold text-slate-950 mb-2" style={{ fontFamily: 'Chivo' }}>Search History</h3>
            <p className="text-sm text-slate-600">Access your previous weather queries</p>
          </Link>

          <Link
            to="/reports"
            data-testid="dashboard-reports-card"
            className="group bg-white p-8 border border-slate-200 hover:border-blue-600 transition-colors"
          >
            <div className="w-12 h-12 bg-blue-50 border border-blue-200 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
              <FilePdf size={24} weight="bold" className="text-blue-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-xl font-semibold text-slate-950 mb-2" style={{ fontFamily: 'Chivo' }}>My Reports</h3>
            <p className="text-sm text-slate-600">Re-download your purchased weather reports anytime</p>
          </Link>

          <Link
            to="/developer"
            data-testid="dashboard-api-card"
            className="group bg-white p-8 border border-slate-200 hover:border-blue-600 transition-colors"
          >
            <div className="w-12 h-12 bg-slate-950 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
              <Code size={24} weight="bold" className="text-white" />
            </div>
            <h3 className="text-xl font-semibold text-slate-950 mb-2" style={{ fontFamily: 'Chivo' }}>API Access</h3>
            <p className="text-sm text-slate-600">Manage API keys and integrate weather data into your apps</p>
          </Link>
        </div>

        {/* Info Box */}
        <div className="mt-12 p-6 bg-blue-50 border border-blue-200">
          <h3 className="text-sm font-semibold text-blue-900 mb-2" style={{ fontFamily: 'Chivo' }}>Need an Official Report?</h3>
          <p className="text-xs text-blue-700 leading-relaxed mb-3">
            After performing a weather lookup, you can purchase a downloadable PDF report for $9.99. Perfect for insurance claims, legal disputes, and professional documentation.
          </p>
          <Link to="/lookup" className="text-xs text-blue-600 hover:text-blue-700 font-medium">
            Start a lookup →
          </Link>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
